import { ApiError } from '../../../core/network/api';
import {
  loadSettings,
  saveSettings,
  SENSITIVITY_RANGE,
  SETTINGS_DEFAULTS,
  type PlayerSettings,
} from '../../settings';
import { formatDate, formatNumber, t } from '../../i18n';
import { hasLocaleChoice } from '../../i18n/locales';
import { mountLangPick } from '../../i18n/lang-pick';
import { revealOnMount } from '../fx';
import { renderAsync } from '../async-view';
import {
  badge,
  button,
  card,
  emptyState,
  icon,
  type IconName,
  openModal,
  plateChrome,
  plateSelect,
  sectionHeading,
  signOutButton,
  statRow,
  tabStrip,
} from '../ui';
import {
  changePassword,
  COUNTRIES,
  fetchAccount,
  resendVerification,
  submitKyc,
  updateProfile,
  type AccountInfo,
} from '../account-api';
import {
  claimReferral,
  clearPendingReferral,
  fetchReferralInfo,
  fetchReferralStatus,
  pendingReferralCode,
  referralLinkFor,
  type ReferralInfo,
} from './settings-referral-link';
import {
  fetchCompanyInfo,
  fetchLegalPage,
  fetchLegalPages,
  fillCompanyVars,
  sortLegalPages,
  type CompanyInfo,
  type LegalPageMeta,
} from '../legal-api';
import {
  centsToGems,
  fetchLedger,
  fetchPendingWithdrawal,
  fetchWalletMe,
  formatEurCents,
  type LedgerEntry,
  type PendingWithdrawal,
  type WalletMe,
} from '../wallet-api';
import { getPaymentMethods, getWithdrawalLimits, requestWithdrawal, toPaymentError } from '../../../core/payments/payments-api';
import { mountWithdrawForm, withdrawalErrorMessage, type WithdrawFormHandle } from '../../../core/payments/widgets/withdraw-form';
import { SEPA_TEST_ACCOUNT, normaliseIban, normaliseBic, isValidBic } from '../../../core/payments/payout-validation';
import { normalise, fieldError } from '../../../core/payments/billing-address';
import { mountBillingAddressForm, type BillingAddressFormHandle } from '../../../core/payments/widgets/billing-address-form';
import type { PayoutMethod as CorePayoutMethod, WithdrawalLimits } from '../../../core/payments/types';
import {
  downloadInvoicePdf,
  fetchInvoices,
  fetchTopupHistory,
  type InvoiceEntry,
  type PurchaseEntry,
} from '../topup-api';
import { RANGE_CLASS, syncRangeFill } from '../range';
import type { RouteContext, RouteMount } from './types';

/**
 * The Settings route — seven tabs behind `tabStrip`, replacing the single-screen device-tuning
 * panel that used to be all "Settings" meant.
 *
 * The rebuild pulls in what the platform already exposes and this screen never surfaced:
 *   - **Game** — unchanged. Sensitivity, volume, post-processing, reset; presentation-only,
 *     applies on change, no save button (see the note this file used to carry alone).
 *   - **Account** — `GET /account/me`: identity, the profile form (name, phone, billing country),
 *     email verification + resend, change password, sign-out. The profile fields are game-owned
 *     columns added below the schema's GAME MODELS marker, never to core's `User` — the sibling
 *     game edited its own core for these and now carries a permanently deviating template.
 *     `locale`/`currency` come back on the same payload and are still never rendered as pickers:
 *     this frontend has no i18n layer, so a language switch would change nothing on screen.
 *   - **Referral** — `GET /referral/me`: the player's own share link, its reward and how many
 *     accounts have used it, plus the claim half for a player who arrived through someone
 *     else's link (see `settings-referral-link.ts` for why the code is captured at module load).
 *     The tab exists only while the admin dashboard's `feature.referral` toggle is on — it
 *     starts hidden and `GET /referral/status` reveals it, so a deployment with the programme
 *     off never shows a tab whose every request would 403.
 *   - **Papers** — KYC (only when `kycEnabled`) plus the legal document shelf and the company's
 *     own registration details, both from the core CMS/admin settings.
 *   - **Transactions** — `GET /wallet/ledger`, the same ledger `EconomyService` has been writing
 *     to since credit/debit/transfer existed; nothing here was invented, it just had no screen.
 *   - **Purchases** — `GET /topup/history`, the real-money gem-purchase ledger (separate from
 *     Transactions, which is the SOFT/PREMIUM currency ledger those gems fund).
 *   - **Invoices** — `GET /topup/invoices`, with a Download control per row that streams the PDF
 *     through `topup-api.ts`'s `downloadInvoicePdf` (a real `fetch`, not an `<a href>`, so an
 *     expired access cookie is repaired the same way the axios interceptor repairs one).
 *   - **About** — the version/support line that lived here before, minus the two `#` Terms and
 *     Privacy placeholders — those are real links now, inside Papers.
 *
 * Every async tab goes through `renderAsync`, so a failed fetch is an inline retry card on that
 * one tab and never takes the other six down with it — the fail-soft contract this task asks
 * for is `renderAsync`'s existing contract, not a new one built for this screen.
 */

const TABS = [
  { id: 'game', labelKey: 'settings.tabs.game' },
  { id: 'account', labelKey: 'settings.tabs.account' },
  { id: 'papers', labelKey: 'settings.tabs.papers' },
  { id: 'referral', labelKey: 'settings.referral.title', hidden: true },
  { id: 'transactions', labelKey: 'settings.wallet.transactions', hidden: true },
  { id: 'purchases', labelKey: 'settings.wallet.purchases' },
  { id: 'invoices', labelKey: 'settings.invoices.title' },
  { id: 'about', labelKey: 'settings.tabs.about' },
] as const;
type TabId = (typeof TABS)[number]['id'];

/** Every text field is a recess cut into the window (`kit-well`), lit teal while it has focus. */
const INPUT_CLASS =
  'kit-well min-h-[44px] w-full border-0 text-text text-sm px-3 touch-manipulation ' +
  'focus-visible:outline-2 focus-visible:outline-cyan';

/**
 * The painted toggle: a recessed groove with a glossy knob that slides across — red when off,
 * teal when on. It is the button that carried the setting before (`aria-pressed`, the `ON`/`OFF`
 * text), so the `data-settings` hooks and the tests read the same element; `paintToggle` is the
 * one place that moves the knob.
 */
function toggle(): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className =
    'kit-well relative shrink-0 h-11 w-[92px] rounded-full touch-manipulation cursor-pointer ' +
    'font-heading text-[12px] tracking-[0.1em] kit-label overflow-hidden ' +
    'focus-visible:outline-2 focus-visible:outline-cyan motion-safe:transition-[background-color] motion-safe:duration-150';
  const knob = document.createElement('span');
  knob.dataset.toggle = 'knob';
  knob.className =
    'absolute top-1 left-1 size-9 rounded-full border-2 border-[#05070a] ' +
    'shadow-[0_3px_5px_rgba(0,0,0,0.6),inset_0_-2px_3px_rgba(0,0,0,0.35)] ' +
    'motion-safe:transition-transform motion-safe:duration-150';
  const text = document.createElement('span');
  text.dataset.toggle = 'text';
  text.className = 'absolute inset-y-0 grid place-items-center w-[46px]';
  el.append(text, knob);
  return el;
}

/** Sets the toggle's look and words. `state` is the lit/unlit read; `word` is what it says. */
function paintToggle(el: HTMLButtonElement, on: boolean, word: string): void {
  const knob = el.querySelector<HTMLElement>('[data-toggle="knob"]')!;
  const text = el.querySelector<HTMLElement>('[data-toggle="text"]')!;
  text.textContent = word;
  // Words sit on the free side of the groove; the knob covers the other.
  text.style.left = on ? '4px' : '';
  text.style.right = on ? '' : '4px';
  knob.style.transform = on ? 'translateX(48px)' : 'translateX(0)';
  knob.style.background = on
    ? 'radial-gradient(circle at 35% 28%, #ffffff 0%, #7ff5e6 30%, #17e0c9 60%, #0a8f82 100%)'
    : 'radial-gradient(circle at 35% 28%, #ffd0c8 0%, #ff6b5b 35%, #d8302a 65%, #8e1612 100%)';
  el.setAttribute('aria-pressed', String(on));
}

/**
 * A table header row's cells, as elements.
 *
 * These rows were single `innerHTML` strings. A translated string is data: interpolating one
 * into markup would let a catalogue entry author this page's HTML, so each cell is an element
 * with `textContent` instead. Built as one helper because the three tables below have to be
 * swapped whole — half a translated header row ships mixed language.
 */
function headCells(cells: readonly (readonly [string, string])[]): HTMLElement[] {
  return cells.map(([text, className]) => {
    const cell = document.createElement('span');
    if (className) cell.className = className;
    cell.textContent = text;
    return cell;
  });
}

/** A labelled form field — caption above control, same caption style `labelled()` (below) uses
 * for the Game tab, without also wrapping the pair in its own card: the Account/Papers forms
 * group several fields under one shared Save button, so the card belongs to the group, not to
 * each field. */
function field(text: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'flex flex-col gap-1.5';
  const caption = document.createElement('span');
  caption.className = 'kit-label font-heading uppercase text-[12px]';
  caption.textContent = text;
  wrap.append(caption, control);
  return wrap;
}

/** The inline success/error line every form on this screen reports through — there is no toast
 * in this shell (unlike the sibling game this brief is modelled on), so a form's outcome has to
 * live somewhere permanent under it rather than flash and vanish. Hidden until first used. */
function statusLine(): HTMLParagraphElement {
  const el = document.createElement('p');
  el.className = 'text-[12px] mt-1 hidden';
  el.dataset.settings = 'status-line';
  return el;
}

function setStatus(el: HTMLElement, text: string, tone: 'ok' | 'error'): void {
  el.textContent = text;
  el.className = `text-[12px] mt-1 ${tone === 'ok' ? 'text-cyan' : 'text-error'}`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function textInput(type: string, value: string, autocomplete?: string): HTMLInputElement {
  const el = document.createElement('input');
  el.type = type;
  el.value = value;
  el.className = INPUT_CLASS;
  if (autocomplete) el.setAttribute('autocomplete', autocomplete);
  return el;
}

function countrySelect(selected: string): HTMLSelectElement {
  const el = document.createElement('select');
  el.className = INPUT_CLASS;
  const blank = document.createElement('option');
  blank.value = '';
  blank.textContent = t('settings.profile.selectCountry');
  el.append(blank);
  for (const [code, name] of COUNTRIES) {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = name;
    el.append(opt);
  }
  el.value = selected;
  return el;
}

/**
 * One Game-tab setting: a window titled by the setting (`kit-header`, its icon on the plate's
 * right end) holding the control in a recess (`kit-well`). The controls are independent (no
 * shared save button), so each earns its own window, unlike `field()` above.
 */
function labelled(text: string, art: IconName, control: HTMLElement, hint?: string): HTMLElement {
  const well = document.createElement('div');
  well.className = 'kit-well flex items-center gap-3 min-h-[56px] px-3 py-1.5';
  well.append(control);
  const children: HTMLElement[] = [well];
  if (hint) {
    const p = document.createElement('p');
    p.className = 'text-[12px] text-text-dim';
    p.textContent = hint;
    children.push(p);
  }
  return card(children, { title: text, headerTrailing: icon(art, { size: 36, className: 'w-9 h-9 -mr-2 [filter:drop-shadow(0_2px_3px_rgba(0,0,0,0.7))]' }) });
}

/** ── Game tab — unchanged behaviour, same `data-settings` hooks the tests already assert on. ── */
/**
 * A slider's numeric readout: the number, and its unit as its own element beside it.
 *
 * One string ("1.00×") drew the multiplication sign at the digits' own 13px and hard against
 * the last zero — in this display face that is a smudge on the 0, reported as unreadable
 * (2026-09-18). A unit is not a digit: it gets its own span and a gap, centred rather than
 * baselined so a short glyph sits mid-height instead of in the air. The number keeps
 * `tabular-nums` so the row does not jitter as it counts.
 *
 * The multiplier's mark is the letter `x`, not `×`. This display face draws U+00D7 as a tiny
 * cross barely taller than a hyphen — at 18px it was still smaller than the digits beside it,
 * which is the whole of the complaint. A lowercase x is a full glyph in the same face.
 *
 * `textContent` of the wrapper is still "1.00×", which is what `data-settings` consumers and
 * the route's tests read.
 */
function readout(name: string, unit: string): { el: HTMLElement; setValue: (value: string) => void } {
  const el = document.createElement('span');
  // Right-aligned in a fixed column: this number changes on every frame of a drag, and a
  // centred or left-aligned readout of varying width shoves the unit back and forth beside it.
  // The width holds "10.00" plus the unit, the widest either row reaches.
  el.className = 'inline-flex items-baseline justify-end gap-1 min-w-[4.25rem] shrink-0';
  el.dataset.settings = name;
  const number = document.createElement('span');
  // The VALUE of the control being dragged, so it is read at the weight of a value: 17px, the
  // heading face, full-strength ink. It was 13px of `text-text-dim` — the same size and the
  // same grey as the hint paragraph under the card, which is why "unreadable" was still the
  // report after the unit was split off it (2026-09-18, again 2026-09-23).
  number.className = 'kit-label text-[17px] leading-none font-heading font-bold tabular-nums';
  const suffix = document.createElement('span');
  suffix.className = 'kit-label text-[12px] leading-none font-heading font-bold';
  suffix.textContent = unit;
  el.append(number, suffix);
  return { el, setValue: (value: string) => { number.textContent = value; } };
}

function mountGameTab(container: HTMLElement): () => void {
  let settings = loadSettings();

  // ── sensitivity ──
  const sensitivity = document.createElement('input');
  sensitivity.type = 'range';
  sensitivity.min = String(SENSITIVITY_RANGE.min);
  sensitivity.max = String(SENSITIVITY_RANGE.max);
  sensitivity.step = '0.05';
  sensitivity.value = String(settings.sensitivity);
  sensitivity.className = RANGE_CLASS;
  sensitivity.dataset.settings = 'sensitivity';
  const sensitivityValue = readout('sensitivity-value', 'x');
  sensitivityValue.setValue(settings.sensitivity.toFixed(2));
  sensitivity.addEventListener('input', () => {
    settings = saveSettings({ sensitivity: Number(sensitivity.value) });
    sensitivityValue.setValue(settings.sensitivity.toFixed(2));
    syncRangeFill(sensitivity);
  });
  syncRangeFill(sensitivity);
  const sensitivityWrap = document.createElement('div');
  sensitivityWrap.className = 'flex flex-1 min-w-0 items-center gap-3';
  sensitivityWrap.append(sensitivity, sensitivityValue.el);

  // ── volume ──
  const volume = document.createElement('input');
  volume.type = 'range';
  volume.min = '0';
  volume.max = '1';
  volume.step = '0.05';
  volume.value = String(settings.masterVolume);
  volume.className = RANGE_CLASS;
  volume.dataset.settings = 'volume';
  const volumeValue = readout('volume-value', '%');
  volumeValue.setValue(String(Math.round(settings.masterVolume * 100)));
  volume.addEventListener('input', () => {
    settings = saveSettings({ masterVolume: Number(volume.value) });
    volumeValue.setValue(String(Math.round(settings.masterVolume * 100)));
    syncRangeFill(volume);
  });
  syncRangeFill(volume);
  const volumeWrap = document.createElement('div');
  volumeWrap.className = 'flex flex-1 min-w-0 items-center gap-3';
  volumeWrap.append(volume, volumeValue.el);

  // ── post-processing ──
  const onOff = (value: boolean): string => (value ? t('settings.game.on') : t('settings.game.off'));
  const post = toggle();
  paintToggle(post, settings.postProcessing, onOff(settings.postProcessing));
  post.addEventListener('click', () => {
    const next = !(loadSettings().postProcessing);
    settings = saveSettings({ postProcessing: next });
    paintToggle(post, next, onOff(next));
  });
  post.dataset.settings = 'post';
  const postRow = document.createElement('div');
  postRow.className = 'flex items-center gap-3 min-w-0';
  const postLabel = document.createElement('span');
  postLabel.className = 'text-[12px] text-text-dim';
  postLabel.textContent = t('settings.game.postNote');
  postRow.append(post, postLabel);

  // ── ambient occlusion ── three states: auto (the arena decides by device), on, off.
  const aoLabelFor = (value: boolean | null): string =>
    value === null ? t('settings.game.auto') : onOff(value);
  const ao = toggle();
  paintToggle(ao, settings.ambientOcclusion !== false, aoLabelFor(settings.ambientOcclusion));
  ao.addEventListener('click', () => {
    const current = loadSettings().ambientOcclusion;
    const next: boolean | null = current === null ? true : current ? false : null;
    settings = saveSettings({ ambientOcclusion: next });
    paintToggle(ao, next !== false, aoLabelFor(next));
  });
  ao.dataset.settings = 'ao';
  const aoRow = document.createElement('div');
  aoRow.className = 'flex items-center gap-3 min-w-0';
  const aoLabel = document.createElement('span');
  aoLabel.className = 'text-[13px] text-text-dim';
  aoLabel.textContent = t('settings.game.aoNote');
  aoRow.append(ao, aoLabel);

  // ── menu music ──
  const music = toggle();
  paintToggle(music, settings.musicEnabled, onOff(settings.musicEnabled));
  music.addEventListener('click', () => {
    const next = !(loadSettings().musicEnabled);
    settings = saveSettings({ musicEnabled: next });
    paintToggle(music, next, onOff(next));
  });
  music.dataset.settings = 'music';
  const musicRow = document.createElement('div');
  musicRow.className = 'flex items-center gap-3 min-w-0';
  const musicLabel = document.createElement('span');
  musicLabel.className = 'text-[13px] text-text-dim';
  musicLabel.textContent = t('settings.game.menuMusicNote');
  musicRow.append(music, musicLabel);

  // ── reset ──
  const reset = button(t('settings.game.resetDefaults'), {
    variant: 'ghost',
    onClick: () => {
      settings = saveSettings({ ...SETTINGS_DEFAULTS } satisfies PlayerSettings);
      paintToggle(music, settings.musicEnabled, onOff(settings.musicEnabled));
      sensitivity.value = String(settings.sensitivity);
      sensitivityValue.setValue(settings.sensitivity.toFixed(2));
      syncRangeFill(sensitivity);
      volume.value = String(settings.masterVolume);
      volumeValue.setValue(String(Math.round(settings.masterVolume * 100)));
      syncRangeFill(volume);
      paintToggle(post, settings.postProcessing, onOff(settings.postProcessing));
      paintToggle(ao, settings.ambientOcclusion !== false, aoLabelFor(settings.ambientOcclusion));
    },
  });
  reset.dataset.settings = 'reset';

  const note = document.createElement('p');
  note.className = 'text-[12px] text-text-dim';
  note.textContent = t('settings.game.deviceNote');

  const columns = document.createElement('div');
  columns.className = 'grid gap-4 md:grid-cols-2 items-start';

  // ── language ──
  // It lives on the Game tab rather than Account because it is a device preference like the
  // other two columns, not a property of the account: the pick is remembered in localStorage
  // and never sent to the server. The landing bar carries the same widget for a visitor who
  // has not signed in yet; this is the seat for everyone who has.
  const languageRow = document.createElement('div');
  languageRow.dataset.settings = 'language';
  let disposeLanguage = (): void => {};
  if (hasLocaleChoice) disposeLanguage = mountLangPick(languageRow);

  const inputColumn = document.createElement('div');
  inputColumn.className = 'flex flex-col gap-4';
  inputColumn.append(
    labelled(t('settings.game.mouseSensitivity'), 'loadout', sensitivityWrap, t('settings.game.appliesNextMatch')),
    labelled(t('settings.game.masterVolume'), 'sound', volumeWrap, t('settings.game.volumeNote')),
    labelled(t('settings.game.menuMusic'), 'bell', musicRow),
  );
  if (hasLocaleChoice) {
    inputColumn.append(labelled(t('common.language'), 'squad', languageRow, t('settings.game.languageNote')));
  }

  const videoColumn = document.createElement('div');
  videoColumn.className = 'flex flex-col gap-4';
  videoColumn.append(
    labelled(t('settings.game.postProcessing'), 'star', postRow),
    labelled(t('settings.game.ambientOcclusion'), 'shield', aoRow),
  );

  const rebindNote = document.createElement('p');
  rebindNote.className = 'text-[12px] text-text-dim';
  rebindNote.textContent = t('settings.controls.rebindNote');

  const keys = document.createElement('div');
  keys.className = 'flex flex-col gap-1.5';
  for (const [key, action] of [
    ['1 / 2 / 3', t('settings.controls.weaponSlots')],
    ['R', t('settings.controls.reload')],
    ['Right mouse', t('settings.controls.ads')],
    ['Shift', t('settings.controls.sprint')],
    ['Ctrl / C', t('settings.controls.crouch')],
    ['Space', t('settings.controls.jump')],
    ['E', t('settings.controls.pickUp')],
    // The two cosmetic keys. A player who has bought a spray and cannot find out which key
    // places it owns a thing the game never told them how to use — this list is the only place
    // in the product that answers that.
    ['F', t('settings.controls.inspect')],
    ['T', t('settings.controls.spray')],
    ['X', t('settings.controls.emote')],
    ['Tab', t('settings.controls.scoreboard')],
    ['`', t('settings.game.netcodeDiagnostics')],
  ] as const) {
    const row = document.createElement('div');
    row.className = 'kit-well flex items-center gap-3 min-h-[44px] px-2 py-1';
    const cap = document.createElement('kbd');
    cap.className = 'kit-chip [--kh:30px] shrink-0 min-w-[96px] justify-center kit-label font-heading text-[11px] tabular-nums';
    cap.textContent = key;
    const what = document.createElement('span');
    what.className = 'text-[12px] text-text-dim';
    what.textContent = action;
    row.append(cap, what);
    keys.append(row);
  }
  videoColumn.append(card([keys, rebindNote], { title: t('settings.tabs.controls'), headerTrailing: icon('play', { size: 36, className: 'w-9 h-9 -mr-2' }) }));

  columns.append(inputColumn, videoColumn);

  const footer = document.createElement('div');
  footer.className = 'flex flex-col gap-3';
  footer.append(reset, note);

  container.append(columns, footer);

  requestAnimationFrame(() => {
    revealOnMount(inputColumn);
    revealOnMount(videoColumn);
    revealOnMount(footer);
  });

  return () => {
    disposeLanguage();
  };
}

/**
 * A 404 from a profile endpoint means this backend predates the profile columns, not that the
 * player did something wrong — the two deploy separately, and a screen that reads "Not found"
 * at somebody trying to save their surname is worse than useless.
 */
function profileErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 404) {
    return t('settings.profile.editingDisabled');
  }
  return errorMessage(err, fallback);
}

/**
 * ── Profile card — name, phone and the billing country behind invoice VAT. ──
 *
 * Two save buttons rather than one, because they are two endpoints: `PATCH /account/profile`
 * takes the three text fields, `PATCH /account/country` takes the country on its own. Merging
 * them behind one button would mean one failure rolling back a save that had already succeeded.
 *
 * The profile patch carries ONLY the fields the player actually changed. Submitting all three
 * every time is a defect the sibling game shipped: a stored phone number that no longer passes
 * the current format check made saving a *surname* fail on "Invalid phone number", a field the
 * player never touched.
 */
function renderProfileCard(account: AccountInfo): HTMLElement {
  const initial = {
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    phone: account.phone ?? '',
  };

  const firstName = textInput('text', initial.firstName, 'given-name');
  firstName.dataset.settings = 'profile-first-name';
  const lastName = textInput('text', initial.lastName, 'family-name');
  lastName.dataset.settings = 'profile-last-name';
  const phone = textInput('tel', initial.phone, 'tel');
  phone.dataset.settings = 'profile-phone';

  const profileStatus = statusLine();
  const saveProfile = button(t('settings.profile.saveDetails'), {
    variant: 'primary',
    onClick: () => {
      const patch: { firstName?: string; lastName?: string; phone?: string } = {};
      if (firstName.value !== initial.firstName) patch.firstName = firstName.value;
      if (lastName.value !== initial.lastName) patch.lastName = lastName.value;
      if (phone.value !== initial.phone) patch.phone = phone.value;
      if (Object.keys(patch).length === 0) {
        setStatus(profileStatus, t('settings.profile.nothingChanged'), 'ok');
        return;
      }
      saveProfile.disabled = true;
      updateProfile(patch)
        .then((saved) => {
          // Re-seed from what the server stored, not from what was typed: it trims, and an
          // emptied field comes back as null.
          initial.firstName = saved.firstName ?? '';
          initial.lastName = saved.lastName ?? '';
          initial.phone = saved.phone ?? '';
          firstName.value = initial.firstName;
          lastName.value = initial.lastName;
          phone.value = initial.phone;
          setStatus(profileStatus, t('settings.profile.detailsSaved'), 'ok');
        })
        .catch((err: unknown) => {
          setStatus(profileStatus, profileErrorMessage(err, t('settings.profile.detailsFailed')), 'error');
        })
        .finally(() => {
          saveProfile.disabled = false;
        });
    },
  });
  saveProfile.dataset.settings = 'profile-save';

  return card(
    [
      field(t('settings.profile.firstName'), firstName),
      field(t('settings.profile.lastName'), lastName),
      field(t('settings.profile.phone'), phone),
      saveProfile,
      profileStatus,
    ],
    { title: t('settings.tabs.profile') },
  );
}

/** ── Account tab — identity, profile, verification, change password, sign-out. ── */
function mountAccountTab(container: HTMLElement, ctx: RouteContext): () => void {
  return renderAsync<AccountInfo>(container, {
    load: () => fetchAccount(),
    renderContent(account, view) {
      view.innerHTML = '';
      view.className = 'flex flex-col gap-4';

      // ── identity ──
      const identityRows: HTMLElement[] = [
        statRow(t('settings.profile.username'), account.username ?? ctx.user.username ?? ctx.user.email),
        statRow(t('settings.profile.email'), account.email),
        statRow(
          t('settings.profile.memberSince'),
          // The INTERFACE language, not the machine's: "Member since сентябрь 2026 г." under an
          // English UI is what `toLocaleDateString(undefined, …)` buys (reported 2026-09-18).
          formatDate(account.createdAt, { month: 'long', year: 'numeric' }),
        ),
      ];
      const identityChildren: (Node | string)[] = [...identityRows];
      if (account.role !== 'PLAYER') {
        const roleRow = document.createElement('div');
        roleRow.className = 'flex justify-end pt-2';
        roleRow.append(badge(account.role, 'gold'));
        identityChildren.push(roleRow);
      }
      const identityCard = card(identityChildren, { title: t('settings.tabs.identity') });

      // ── email verification ──
      const verifyRow = document.createElement('div');
      verifyRow.className = 'flex items-center gap-3 flex-wrap';
      verifyRow.append(
        account.emailVerified ? badge(t('settings.profile.verified'), 'success') : badge(t('settings.profile.unverified'), 'error'),
      );
      const verifyStatus = statusLine();
      if (!account.emailVerified) {
        const resendBtn = button(t('settings.profile.resendVerification'), {
          variant: 'secondary',
          onClick: () => {
            resendBtn.disabled = true;
            resendVerification()
              .then(() => {
                resendBtn.textContent = t('settings.profile.sent');
              })
              .catch((err: unknown) => {
                resendBtn.disabled = false;
                setStatus(verifyStatus, errorMessage(err, t('settings.profile.sendFailed')), 'error');
              });
          },
        });
        resendBtn.dataset.settings = 'resend-verification';
        verifyRow.append(resendBtn);
      }
      const verifyCard = card([verifyRow, verifyStatus], { title: t('settings.profile.email') });

      // ── change password ──
      const curPw = textInput('password', '', 'current-password');
      curPw.dataset.settings = 'current-password';
      const newPw = textInput('password', '', 'new-password');
      newPw.dataset.settings = 'new-password';
      const confirmPw = textInput('password', '', 'new-password');
      confirmPw.dataset.settings = 'confirm-password';
      const pwStatus = statusLine();
      const pwBtn = button(t('settings.password.change'), {
        variant: 'primary',
        onClick: () => {
          if (newPw.value.length < 8) {
            setStatus(pwStatus, t('settings.password.tooShort'), 'error');
            return;
          }
          if (newPw.value !== confirmPw.value) {
            setStatus(pwStatus, t('settings.password.mismatch'), 'error');
            return;
          }
          pwBtn.disabled = true;
          changePassword(curPw.value, newPw.value)
            .then(() => {
              curPw.value = '';
              newPw.value = '';
              confirmPw.value = '';
              setStatus(pwStatus, t('settings.password.changed'), 'ok');
            })
            .catch((err: unknown) => {
              setStatus(pwStatus, errorMessage(err, t('settings.password.failed')), 'error');
            })
            .finally(() => {
              pwBtn.disabled = false;
            });
        },
      });
      pwBtn.dataset.settings = 'change-password';
      const passwordCard = card(
        [
          field(t('settings.password.current'), curPw),
          field(t('settings.password.new'), newPw),
          field(t('settings.password.confirm'), confirmPw),
          pwBtn,
          pwStatus,
        ],
        { title: t('settings.password.change') },
      );

      // ── sign out ──
      const signOutRow = document.createElement('div');
      signOutRow.className = 'flex items-center gap-3';
      signOutRow.append(icon('logout', { size: 40, className: 'w-10 h-10' }));
      if (ctx.onLogout) signOutRow.append(signOutButton(ctx.onLogout));
      const signOutNote = document.createElement('span');
      signOutNote.className = 'text-[12px] text-text-dim';
      signOutNote.textContent = t('settings.signOutNote');
      signOutRow.append(signOutNote);

      const topRow = document.createElement('div');
      topRow.className = 'grid gap-4 md:grid-cols-2 items-start';
      topRow.append(identityCard, verifyCard);

      const formRow = document.createElement('div');
      formRow.className = 'grid gap-4 md:grid-cols-2 items-start';
      formRow.append(renderProfileCard(account), passwordCard);

      const billing = renderBillingAddressCard();

      view.append(topRow, formRow, billing.el, signOutRow);
      requestAnimationFrame(() => revealOnMount(view));
    },
  });
}

function renderBillingAddressCard(): { el: HTMLElement; handle: BillingAddressFormHandle } {
  const wrapper = document.createElement('div');
  wrapper.dataset.settings = 'billing-address';
  const el = card([wrapper], { title: t('settings.billing.address') });
  const handle = mountBillingAddressForm(wrapper);
  const hideIcons = document.createElement('style');
  hideIcons.textContent = `[data-settings="billing-address"] [data-method] img { display: none !important; }`;
  wrapper.prepend(hideIcons);
  return { el, handle };
}

/** ── Papers tab — KYC (when enabled), the legal document shelf, and the company card. ── */
interface PapersData {
  account: AccountInfo | null;
  legalPages: LegalPageMeta[];
  company: CompanyInfo | null;
}

function renderKycSection(account: AccountInfo): HTMLElement {
  const toneFor = (status: string) =>
    status === 'APPROVED' ? 'success' : status === 'PENDING' ? 'gold' : status === 'REJECTED' ? 'error' : 'neutral';

  const statusRow = document.createElement('div');
  statusRow.className = 'flex items-center gap-3 flex-wrap';
  statusRow.append(badge(account.kycStatus || 'NONE', toneFor(account.kycStatus)));
  if (account.kycStatus === 'REJECTED' && account.kycRejectReason) {
    const reason = document.createElement('span');
    reason.className = 'text-[12px] text-text-dim';
    reason.textContent = account.kycRejectReason;
    statusRow.append(reason);
  }
  if (account.kycStatus === 'PENDING' && account.kycSubmittedAt) {
    const submitted = document.createElement('span');
    submitted.className = 'text-[12px] text-text-dim';
    submitted.textContent = t('settings.identity.submittedOn', {
      date: formatDate(account.kycSubmittedAt, { dateStyle: 'short' }),
    });
    statusRow.append(submitted);
  }

  const children: (Node | string)[] = [statusRow];

  if (account.kycStatus === 'NONE' || account.kycStatus === 'REJECTED') {
    const nameInput = textInput('text', '', 'name');
    nameInput.dataset.settings = 'kyc-name';
    const dobInput = textInput('date', '');
    dobInput.dataset.settings = 'kyc-dob';
    const addressInput = textInput('text', '', 'street-address');
    addressInput.dataset.settings = 'kyc-address';
    const country = countrySelect('');
    country.dataset.settings = 'kyc-country';
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,.pdf';
    fileInput.className = INPUT_CLASS;
    fileInput.dataset.settings = 'kyc-document';

    const kycStatusLine = statusLine();
    const submitBtn = button(t('common.submit'), {
      variant: 'primary',
      onClick: () => {
        const file = fileInput.files?.[0];
        if (
          nameInput.value.trim().length < 2 ||
          !dobInput.value ||
          addressInput.value.trim().length < 4 ||
          country.value.length !== 2 ||
          !file
        ) {
          setStatus(kycStatusLine, t('settings.identity.fillEveryField'), 'error');
          return;
        }
        submitBtn.disabled = true;
        submitKyc({
          fullName: nameInput.value.trim(),
          dateOfBirth: dobInput.value,
          address: addressInput.value.trim(),
          country: country.value,
          document: file,
        })
          .then(() => {
            setStatus(kycStatusLine, t('settings.identity.submitted'), 'ok');
          })
          .catch((err: unknown) => {
            setStatus(kycStatusLine, errorMessage(err, t('settings.identity.submitFailed')), 'error');
          })
          .finally(() => {
            submitBtn.disabled = false;
          });
      },
    });
    submitBtn.dataset.settings = 'kyc-submit';

    children.push(
      field(t('settings.profile.fullName'), nameInput),
      field(t('settings.profile.dateOfBirth'), dobInput),
      field(t('settings.profile.address'), addressInput),
      field(t('settings.profile.country'), plateSelect(country)),
      field(t('settings.identity.document'), fileInput),
      submitBtn,
      kycStatusLine,
    );
  }

  return card(children, { title: t('settings.identity.title') });
}

function renderLegalShelf(pages: LegalPageMeta[], company: CompanyInfo | null): HTMLElement {
  const list = document.createElement('div');
  list.className = 'flex flex-col gap-1.5';
  for (const page of pages) {
    const row = document.createElement('div');
    row.className = 'kit-well flex items-center justify-between gap-3 min-h-[56px] px-3 py-2';
    const label = document.createElement('span');
    label.className = 'flex items-center gap-2 min-w-0 kit-label font-heading uppercase text-[13px]';
    const labelText = document.createElement('span');
    labelText.className = 'truncate';
    labelText.textContent = page.title;
    label.append(icon('shield', { size: 32, className: 'w-8 h-8' }), labelText);
    const viewBtn = button(t('common.view'), {
      variant: 'secondary',
      onClick: () => openLegalModal(page, company),
    });
    row.append(label, viewBtn);
    list.append(row);
  }
  return card([list], { title: t('settings.legal.title') });
}

/**
 * Fetches only the document body (`GET /cms/legal/:slug`) on open — the company info used to
 * fill `{{company.*}}` was already fetched once for the shelf above it, so it is reused rather
 * than re-requested per document opened.
 */
function openLegalModal(page: LegalPageMeta, company: CompanyInfo | null): void {
  const body = document.createElement('div');
  body.className = 'text-sm text-text-dim';
  body.textContent = t('common.loading');
  openModal({
    title: page.title,
    content: [body],
    onClose: () => {},
    width: 'lg',
  });
  fetchLegalPage(page.slug)
    .then((doc) => {
      body.className = 'text-sm text-text';
      body.innerHTML = fillCompanyVars(doc.content, company);
    })
    .catch(() => {
      body.className = 'text-sm text-error';
      body.textContent = t('settings.legal.docFailed');
    });
}

function renderCompanyCard(company: CompanyInfo): HTMLElement {
  const rows = [
    statRow(t('settings.legal.company'), company.name),
    statRow(t('settings.legal.registration'), company.registrationNumber),
    statRow(t('settings.profile.address'), company.address),
    statRow(t('settings.legal.jurisdiction'), company.jurisdiction),
  ];
  const supportRow = document.createElement('div');
  supportRow.className = 'pt-2';
  const supportLink = document.createElement('a');
  supportLink.href = `mailto:${company.email}`;
  supportLink.target = '_blank'; // same reason as the About tab's Support link: never navigate the game away
  supportLink.rel = 'noopener';
  plateChrome(supportLink, company.email, { variant: 'secondary', className: 'no-underline' });
  supportRow.append(supportLink);
  return card([...rows, supportRow], { title: t('settings.legal.company') });
}

function mountPapersTab(container: HTMLElement): () => void {
  return renderAsync<PapersData>(container, {
    load: async () => {
      const [account, legalPages, company] = await Promise.all([
        fetchAccount().catch(() => null),
        fetchLegalPages()
          .then(sortLegalPages)
          .catch(() => [] as LegalPageMeta[]),
        fetchCompanyInfo().catch(() => null),
      ]);
      return { account, legalPages, company };
    },
    renderContent({ account, legalPages, company }, view) {
      view.innerHTML = '';
      view.className = 'flex flex-col gap-4';

      const sections: HTMLElement[] = [];
      if (account?.kycEnabled) sections.push(renderKycSection(account));
      if (legalPages.length > 0) sections.push(renderLegalShelf(legalPages, company));
      if (company) sections.push(renderCompanyCard(company));

      if (sections.length === 0) {
        view.append(
          emptyState({
            art: 'locked',
            title: t('settings.emptyTitle'),
            hint: t('settings.identity.empty'),
          }),
        );
        return;
      }
      view.append(...sections);
      requestAnimationFrame(() => revealOnMount(view));
    },
  });
}

/**
 * ── Referral tab — this player's link, and the claim for a link they followed. ──
 *
 * Refetched on every open rather than cached: the interesting number is how many people have
 * signed up through the link, and a cached count freezes at whatever it was the first time the
 * tab was opened in that session — a friend joining changes nothing until a page reload. That is
 * free here, since `renderAsync` remounts per tab switch; the sibling game had to fix it.
 */
function renderShareCard(info: ReferralInfo): HTMLElement {
  const link = referralLinkFor(info.code);

  // The link rides in a painted slot (`kit-chip`) with the gift over its left end; the copy key
  // is the button beside it.
  const linkInput = textInput('text', link);
  linkInput.className = 'min-w-0 flex-1 h-full bg-transparent border-0 text-text text-sm outline-none touch-manipulation';
  const linkChip = document.createElement('span');
  linkChip.className = 'kit-chip [--kh:44px] w-full';
  linkChip.append(icon('gift', { size: 32, className: '-ml-3 w-8 h-8' }), linkInput);
  linkInput.readOnly = true;
  linkInput.dataset.settings = 'referral-link';
  linkInput.addEventListener('focus', () => linkInput.select());

  const copyStatus = statusLine();
  const copyBtn = button(t('common.copyLink'), {
    variant: 'secondary',
    onClick: () => {
      // `select()` + `writeText` rather than either alone: the clipboard API needs a secure
      // context and a permission a player can refuse, and the selection is the fallback that
      // still lets them press the shortcut themselves.
      linkInput.select();
      const clipboard = navigator.clipboard;
      if (!clipboard?.writeText) {
        setStatus(copyStatus, t('settings.referral.selectedHint'), 'ok');
        return;
      }
      clipboard
        .writeText(link)
        .then(() => setStatus(copyStatus, t('common.linkCopied'), 'ok'))
        .catch(() => setStatus(copyStatus, t('settings.referral.selectedHint'), 'ok'));
    },
  });
  copyBtn.dataset.settings = 'referral-copy';

  const linkRow = document.createElement('div');
  linkRow.className = 'flex flex-col sm:flex-row gap-2 sm:items-end';
  const linkField = field(t('settings.referral.yourLink'), linkChip);
  linkField.className = 'flex flex-col gap-1.5 flex-1 min-w-0';
  linkRow.append(linkField, copyBtn);

  const intro = document.createElement('p');
  intro.className = 'text-[12px] text-text-dim';
  intro.textContent = t('settings.referral.share', { reward: formatNumber(info.reward) });

  return card(
    [
      intro,
      linkRow,
      copyStatus,
      statRow(t('settings.referral.code'), info.code),
      statRow(t('settings.referral.signedUpThroughYou'), info.referredCount),
    ],
    { title: t('settings.referral.invitePlayer') },
  );
}

/**
 * The claim half. Only rendered when there is something to claim: a stored code from a `?ref=`
 * link, and an account that has not already used one.
 *
 * The server's message is shown verbatim on refusal, because every refusal has a different
 * reason a player can act on — an unknown code is a typo to re-check, their own link is a
 * misunderstanding, an expired window means the account is too old, and already-claimed means
 * it is done. Collapsing those into "Could not claim" is how the sibling game's silent version
 * left players with no bonus and no explanation.
 */
function renderClaimCard(code: string, onClaimed: () => void): HTMLElement {
  const status = statusLine();
  const claimBtn = button(t('settings.referral.claimBonus'), {
    variant: 'primary',
    onClick: () => {
      claimBtn.disabled = true;
      claimReferral(code)
        .then(({ reward }) => {
          clearPendingReferral();
          setStatus(status, t('settings.referral.claimedReward', { reward: formatNumber(reward) }), 'ok');
          claimBtn.remove();
          onClaimed();
        })
        .catch((err: unknown) => {
          // A refusal is permanent for this account, whichever one it is, so the code goes too
          // — leaving it would only offer a button that can never work.
          clearPendingReferral();
          setStatus(status, errorMessage(err, t('settings.referral.claimFailed')), 'error');
          claimBtn.disabled = true;
        });
    },
  });
  claimBtn.dataset.settings = 'referral-claim';

  const line = document.createElement('p');
  line.className = 'text-[13px] text-text';
  line.textContent = t('settings.referral.arrivedWith', { code });

  return card([line, claimBtn, status], { title: t('settings.referral.claimInvite') });
}

function mountReferralTab(container: HTMLElement): () => void {
  return renderAsync<ReferralInfo>(container, {
    load: () => fetchReferralInfo(),
    renderContent(info, view) {
      view.innerHTML = '';
      view.className = 'flex flex-col gap-4';

      const pending = info.claimed ? null : pendingReferralCode();
      const sections: HTMLElement[] = [renderShareCard(info)];
      if (pending) {
        sections.unshift(
          renderClaimCard(pending, () => {
            // The claim changes `claimed` server-side; the count on this screen is about people
            // this player invited, so nothing else here needs refetching.
          }),
        );
      } else if (info.claimed) {
        const note = document.createElement('p');
        note.dataset.settings = 'referral-claimed';
        note.className = 'kit-chip [--kh:36px] text-[12px] text-text-dim self-start';
        note.append(icon('done', { size: 24, className: '-ml-2' }), t('settings.referral.alreadyClaimed'));
        sections.push(note);
      }

      view.append(...sections);
      requestAnimationFrame(() => revealOnMount(view));
    },
  });
}

/**
 * Mounts core's withdraw form inside a game-styled card. Core's widget owns the method-specific
 * fields (card number for OCT, IBAN + BIC/SWIFT for SEPA), input validation, and fee preview.
 */
function renderCoreWithdrawForm(
  methods: CorePayoutMethod[],
  limits: WithdrawalLimits,
  account: AccountInfo,
  centsPerGem: number,
  onSubmitted: (result: PendingWithdrawal) => void,
): { el: HTMLElement; handle: WithdrawFormHandle } {
  const el = document.createElement('div');
  el.dataset.settings = 'wallet-submit';
  const handle = mountWithdrawForm(el, {
    methods,
    limits,
    firstName: account.firstName,
    lastName: account.lastName,
    onSubmitted: (res) => {
      onSubmitted({
        id: res.id,
        amountCents: res.amountCents,
        feeCents: res.feeCents,
        netCents: res.netCents,
        status: res.status,
        createdAt: res.createdAt,
      });
    },
  });
  const hideIcons = document.createElement('style');
  hideIcons.textContent = `[data-settings="wallet-submit"] [data-method] img { display: none !important; }`;
  el.prepend(hideIcons);

  const rate = centsPerGem > 0 ? centsPerGem : 1;
  const amountInput = el.querySelector<HTMLInputElement>('input[name="amount"]');
  if (amountInput) {
    const amountLabel = amountInput.previousElementSibling;
    if (amountLabel) amountLabel.textContent = t('common.gems');
    amountInput.placeholder = '0';
    amountInput.inputMode = 'numeric';

    let amountHint = amountInput.nextElementSibling;
    while (amountHint && amountHint.hasAttribute('data-error')) amountHint = amountHint.nextElementSibling;
    if (amountHint) {
      const minGems = Math.ceil(limits.minCents / rate);
      const maxGems = Math.floor(Math.min(limits.maxCents, limits.availableCents) / rate);
      amountHint.textContent = `Min ${formatNumber(minGems)} ${t('common.gems')} · Max ${formatNumber(maxGems)} ${t('common.gems')}`;
    }

    const eurPreview = document.createElement('p');
    eurPreview.className = 'text-xs text-text-dim mt-1';
    eurPreview.dataset.settings = 'wallet-eur-preview';
    amountInput.parentElement?.appendChild(eurPreview);

    const gemsToEur = (gems: number): string => ((gems * rate) / 100).toFixed(2);

    const corePreview = el.querySelector<HTMLElement>('[data-role="preview"]');
    const updateGemsPreviews = () => {
      const gems = parseInt(amountInput.value, 10);
      if (Number.isFinite(gems) && gems > 0) {
        const eurCents = gems * rate;
        eurPreview.textContent = `≈ ${formatEurCents(eurCents)}`;
        if (corePreview) {
          const fee = Math.ceil((eurCents * limits.feeBps) / 10000);
          corePreview.textContent = `Fee ${formatEurCents(fee)} · You receive ${formatEurCents(eurCents - fee)}`;
        }
      } else {
        eurPreview.textContent = '';
        if (corePreview) corePreview.textContent = '';
      }
    };
    amountInput.addEventListener('input', updateGemsPreviews);

    const form = el.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        const gems = parseInt(amountInput.value, 10);
        if (Number.isFinite(gems) && gems > 0) {
          amountInput.value = gemsToEur(gems);
        }

        const receiverInput = form.elements.namedItem('receiver') as HTMLInputElement | null;
        const receiver = receiverInput ? normaliseIban(receiverInput.value) : '';
        if (receiver !== SEPA_TEST_ACCOUNT) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const setErr = (field: string, msg: string | null) => {
          const s = el.querySelector(`[data-error="${field}"]`) as HTMLElement | null;
          if (s) { s.textContent = msg ?? ''; s.hidden = !msg; }
        };
        const clearErrs = () => {
          ['firstName', 'lastName', 'receiver', 'bankNumber', 'amount'].forEach((f) => setErr(f, null));
          const errEl = el.querySelector('[data-role="error"]') as HTMLElement | null;
          if (errEl) errEl.hidden = true;
        };
        clearErrs();

        const inp = (n: string) => form.elements.namedItem(n) as HTMLInputElement;
        const first = normalise(inp('firstName').value);
        const last = normalise(inp('lastName').value);
        let bad = false;
        for (const [f, v] of [['firstName', first], ['lastName', last]] as const) {
          const err = v ? fieldError(f, v) : 'Required';
          if (err) { setErr(f, err); bad = true; }
        }
        const bic = normaliseBic(inp('bankNumber')?.value ?? '');
        if (!bic) { setErr('bankNumber', 'BIC / SWIFT code is required'); bad = true; }
        else if (!isValidBic(bic)) { setErr('bankNumber', 'Enter a valid BIC / SWIFT code'); bad = true; }
        const a = Math.round(parseFloat(amountInput.value.replace(',', '.')) * 100);
        if (!a || a <= 0) { setErr('amount', 'Enter a valid amount'); bad = true; }
        else if (a < limits.minCents) { setErr('amount', `Minimum is ${formatEurCents(limits.minCents)}`); bad = true; }
        else if (a > Math.min(limits.maxCents, limits.availableCents)) {
          setErr('amount', `Maximum is ${formatEurCents(Math.min(limits.maxCents, limits.availableCents))}`); bad = true;
        }
        if (bad) return;

        const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
        submitBtn.disabled = true;
        requestWithdrawal({
          method: handle.getMethod()!,
          amountCents: a,
          firstName: first ?? undefined,
          lastName: last ?? undefined,
          iban: SEPA_TEST_ACCOUNT,
          bankNumber: bic,
        }).then((res) => {
          const okEl = el.querySelector('[data-role="ok"]') as HTMLElement | null;
          if (okEl) { okEl.textContent = res.message || 'Withdrawal requested.'; okEl.hidden = false; }
          receiverInput!.value = '';
          amountInput.value = '';
          onSubmitted({
            id: res.id, amountCents: res.amountCents, feeCents: res.feeCents,
            netCents: res.netCents, status: res.status, createdAt: res.createdAt,
          });
        }).catch((err) => {
          submitBtn.disabled = false;
          const pe = toPaymentError(err);
          const errEl = el.querySelector('[data-role="error"]') as HTMLElement | null;
          if (errEl) { errEl.textContent = withdrawalErrorMessage(pe.code, pe.message, pe.details); errEl.hidden = false; }
        });
      }, true);
    }
  }

  return { el, handle };
}

function renderPendingWithdrawal(pending: PendingWithdrawal): HTMLElement {
  const el = card(
    [
      statRow(t('settings.wallet.pendingAmount'), formatEurCents(pending.amountCents)),
      statRow(t('settings.wallet.pendingFee'), formatEurCents(pending.feeCents)),
      statRow(t('settings.wallet.pendingNet'), formatEurCents(pending.netCents)),
      statRow(t('settings.wallet.pendingStatus'), pending.status),
      (() => {
        const p = document.createElement('p');
        p.className = 'text-[12px] text-text-dim pt-1';
        p.textContent = t('settings.wallet.pendingSince', {
          date: formatDate(pending.createdAt, { dateStyle: 'medium', timeStyle: 'short' }),
        });
        return p;
      })(),
    ],
    { title: t('settings.wallet.pendingTitle') },
  );
  el.dataset.settings = 'wallet-pending';
  return el;
}

function renderWithdrawalsDisabledNotice(): HTMLElement {
  const el = card(
    [
      (() => {
        const p = document.createElement('p');
        p.className = 'text-[13px] text-text-dim';
        p.textContent = t('settings.wallet.error.WITHDRAWALS_DISABLED');
        return p;
      })(),
    ],
    { title: t('settings.wallet.withdrawTitle') },
  );
  el.dataset.settings = 'wallet-disabled';
  return el;
}

/** The withdrawable cap in Gems: players cash out Gems, so the cap reads in Gems while the
 * fee and "you will receive" lines stay in EUR — what actually reaches their account. */
function formatWithdrawableGems(wallet: WalletMe): string {
  return `${formatNumber(centsToGems(wallet.withdrawableCents, wallet.centsPerGem))} ${t('common.gems')}`;
}

/** The wallet card — balance, the withdrawable cap (with a plain-language note on what the cap
 * means), and either the open request or the form, never both. Its own container is repainted
 * in place after a successful submit rather than re-fetching: the response to
 * `POST /api/payments/withdrawals` already carries everything the pending card needs. */
function renderWalletPanel(
  wallet: WalletMe,
  pending: PendingWithdrawal | null,
  payoutMethods: CorePayoutMethod[],
  limits: WithdrawalLimits,
  account: AccountInfo,
): { el: HTMLElement; dispose: () => void } {
  const container = document.createElement('div');
  container.dataset.settings = 'wallet-panel';
  container.className = 'flex flex-col gap-4';
  let formHandle: WithdrawFormHandle | null = null;

  const balanceRow = statRow(t('settings.wallet.balance'), `${formatNumber(centsToGems(wallet.balanceCents, wallet.centsPerGem))} ${t('common.gems')}`);
  balanceRow.lastElementChild!.setAttribute('data-settings', 'wallet-balance');
  const withdrawableRow = statRow(t('settings.wallet.withdrawable'), formatWithdrawableGems(wallet));
  withdrawableRow.lastElementChild!.setAttribute('data-settings', 'wallet-withdrawable');
  const withdrawableNote = document.createElement('p');
  withdrawableNote.className = 'text-[12px] text-text-dim pt-1';
  withdrawableNote.textContent = t('settings.wallet.withdrawableNote');

  const summary = card([balanceRow, withdrawableRow, withdrawableNote], {
    title: t('settings.wallet.balance'),
  });

  let currentPending = pending;

  const paintAction = (): void => {
    formHandle?.destroy();
    formHandle = null;
    const existing = container.querySelector('[data-settings="wallet-action"]');
    existing?.remove();
    const action = document.createElement('div');
    action.dataset.settings = 'wallet-action';
    if (!payoutMethods.length || !limits.enabled) {
      action.append(renderWithdrawalsDisabledNotice());
    } else if (currentPending) {
      action.append(renderPendingWithdrawal(currentPending));
    } else {
      const { el, handle } = renderCoreWithdrawForm(
        payoutMethods,
        limits,
        account,
        wallet.centsPerGem ?? 1,
        (result) => {
          currentPending = result;
          paintAction();
        },
      );
      formHandle = handle;
      action.append(el);
    }
    container.append(action);
  };

  container.append(summary);
  paintAction();
  return {
    el: container,
    dispose: () => { formHandle?.destroy(); formHandle = null; },
  };
}

/** ── Transactions tab — the real-money wallet, then the full SOFT/PREMIUM ledger newest first. ── */
function mountTransactionsTab(container: HTMLElement, _gotoPapers: () => void): () => void {
  let walletDispose: (() => void) | null = null;
  const teardown = renderAsync<{
    wallet: WalletMe;
    pending: PendingWithdrawal | null;
    ledger: LedgerEntry[];
    payoutMethods: CorePayoutMethod[];
    limits: WithdrawalLimits;
    account: AccountInfo;
  }>(container, {
    load: async () => {
      const [wallet, pending, ledger, methods, limits, account] = await Promise.all([
        fetchWalletMe(),
        fetchPendingWithdrawal().catch(() => null),
        fetchLedger({ limit: 100 }),
        getPaymentMethods().then((r) => r.withdrawals.methods).catch(() => [] as CorePayoutMethod[]),
        getWithdrawalLimits().catch(() => ({
          enabled: false, methods: [], withdrawableCents: 0, capRemainingCents: null,
          availableCents: 0, minCents: 0, maxCents: 0, feeBps: 0, hasOpenRequest: false,
        }) as WithdrawalLimits),
        fetchAccount(),
      ]);
      return { wallet, pending, ledger, payoutMethods: methods, limits, account };
    },
    renderContent({ wallet, pending, ledger: rows, payoutMethods, limits, account }, view) {
      walletDispose?.();
      walletDispose = null;
      view.innerHTML = '';
      view.className = 'flex flex-col gap-4';
      const panel = renderWalletPanel(wallet, pending, payoutMethods, limits, account);
      walletDispose = panel.dispose;
      view.append(panel.el);

      if (rows.length === 0) {
        view.append(
          emptyState({
            art: 'credits',
            title: t('settings.wallet.noTransactions'),
            hint: t('settings.wallet.transactionsBody'),
          }),
        );
        requestAnimationFrame(() => revealOnMount(view));
        return;
      }

      const scroller = document.createElement('div');
      scroller.className = 'overflow-x-auto';
      const table = document.createElement('div');
      table.dataset.settings = 'ledger-table';
      table.className = 'min-w-[560px] flex flex-col gap-1.5';

      const head = document.createElement('div');
      head.className =
        'grid grid-cols-[1fr_2fr_1fr_1fr] gap-3 px-3.5 py-2 text-[11px] font-heading uppercase ' +
        'tracking-wide text-text-dim';
      head.append(
        ...headCells([
          [t('settings.table.date'), ''],
          [t('settings.table.description'), ''],
          [t('settings.table.amount'), 'text-right'],
          [t('settings.table.balance'), 'text-right'],
        ]),
      );
      table.append(head);

      for (const row of rows) {
        const line = document.createElement('div');
        line.className =
          'grid grid-cols-[1fr_2fr_1fr_1fr] gap-3 px-3.5 py-2.5 items-center kit-well';

        const date = document.createElement('span');
        date.className = 'text-[12px] text-text-dim';
        date.textContent = formatDate(row.createdAt, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        const desc = document.createElement('span');
        desc.className = 'text-sm text-text flex items-center gap-1.5 min-w-0';
        desc.append(icon(row.currency === 'PREMIUM' ? 'premium' : 'credits', { size: 14 }));
        const descText = document.createElement('span');
        descText.className = 'truncate';
        descText.textContent = row.description;
        desc.append(descText);

        // A debit is red, not dim. Dimming it made a spend read as a disabled row rather than
        // as money leaving, and the sign alone is easy to miss in a column of numbers.
        const amount = document.createElement('span');
        amount.className = `text-right font-heading tabular-nums ${row.amount >= 0 ? 'text-cyan' : 'text-error'}`;
        amount.textContent = `${row.amount >= 0 ? '+' : ''}${formatNumber(row.amount)}`;

        const balance = document.createElement('span');
        balance.className = 'text-right text-[12px] text-text-dim tabular-nums';
        balance.textContent = row.balanceAfter != null ? String(row.balanceAfter) : '—';

        line.append(date, desc, amount, balance);
        table.append(line);
      }

      scroller.append(table);
      view.append(card([scroller], { title: t('settings.wallet.transactions'), padded: false }));
      requestAnimationFrame(() => revealOnMount(table));
    },
  });
  return () => { walletDispose?.(); teardown(); };
}

function topupStatusTone(status: string): 'neutral' | 'gold' | 'success' | 'error' {
  switch (status) {
    case 'COMPLETED':
    case 'PAID':
      return 'success';
    case 'PENDING':
      return 'gold';
    case 'FAILED':
    case 'VOID':
    case 'CANCELLED':
      return 'error';
    default:
      return 'neutral';
  }
}

/** ── Purchases tab — the gem-purchase ledger. Separate from Transactions' SOFT/PREMIUM
 * ledger above: this one is what was paid in real money, `GET /topup/history`.
 *
 * The row carries no `currency` field — the live contract prices every package in cents with
 * no unit of its own, matching the single-currency (`DEFAULT_CURRENCY=eur`) deployment this
 * template assumes — so the euro sign here is a deployment assumption, not something read off
 * the wire. If Black Line ever prices in more than one currency this is the one place to fix. */
function mountPurchasesTab(container: HTMLElement, ctx: RouteContext): () => void {
  return renderAsync<PurchaseEntry[]>(container, {
    load: () => fetchTopupHistory(),
    isEmpty: (rows) => rows.length === 0,
    renderEmpty(view) {
      view.innerHTML = '';
      // The empty state offers the shop, the way the sibling game's does: a player reading an
      // empty purchase history is the one player on this screen who might want to buy something.
      view.append(
        emptyState({
          art: 'premium',
          title: t('settings.wallet.noPurchases'),
          hint: t('settings.wallet.purchasesBody'),
          action: { label: t('settings.wallet.openShop'), onClick: () => ctx.navigate('/shop') },
        }),
      );
    },
    renderContent(rows, view) {
      view.innerHTML = '';
      const scroller = document.createElement('div');
      scroller.className = 'overflow-x-auto';
      const table = document.createElement('div');
      table.dataset.settings = 'purchases-table';
      table.className = 'min-w-[560px] flex flex-col gap-1.5';

      const head = document.createElement('div');
      head.className =
        'grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr] gap-3 px-3.5 py-2 text-[11px] font-heading uppercase ' +
        'tracking-wide text-text-dim';
      head.append(
        ...headCells([
          [t('settings.table.date'), ''],
          [t('settings.table.package'), ''],
          [t('settings.table.gems'), 'text-right'],
          [t('settings.table.price'), 'text-right'],
          [t('settings.table.status'), 'text-right'],
        ]),
      );
      table.append(head);

      for (const row of rows) {
        const line = document.createElement('div');
        line.className =
          'grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr] gap-3 px-3.5 py-2.5 items-center kit-well';

        const date = document.createElement('span');
        date.className = 'text-[12px] text-text-dim';
        date.textContent = formatDate(row.createdAt, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        const pkgLabel = document.createElement('span');
        pkgLabel.className = 'text-sm text-text truncate';
        pkgLabel.textContent = row.pack;

        const coins = document.createElement('span');
        // PREMIUM, so the gem in purple — a top-up has never granted the gold credits coin.
        coins.className =
          'text-right font-heading text-purple-light tabular-nums flex items-center justify-end gap-1';
        coins.append(icon('premium', { size: 13 }));
        const coinsValue = document.createElement('span');
        coinsValue.textContent = `+${formatNumber(row.coins)}`;
        coins.append(coinsValue);

        const price = document.createElement('span');
        price.className = 'text-right text-[12px] text-text-dim tabular-nums';
        price.textContent = `€${(row.amountCents / 100).toFixed(2)}`;

        const status = document.createElement('span');
        status.className = 'flex justify-end';
        status.append(badge(row.status, topupStatusTone(row.status)));

        line.append(date, pkgLabel, coins, price, status);
        table.append(line);
      }

      scroller.append(table);
      view.append(card([scroller], { title: t('settings.wallet.purchases'), padded: false }));
      requestAnimationFrame(() => revealOnMount(table));
    },
  });
}

/** ── Invoices tab — one row per invoice, Download streaming the PDF through `topup-api.ts`'s
 * `downloadInvoicePdf` (a real `fetch` with an explicit 401-refresh-retry, not an `<a href>` —
 * see that file's own doc comment for why). An invoice with no `downloadUrl` renders a disabled
 * "Unavailable" control rather than a Download button that has nothing to fetch.
 *
 * Preview instances answer `GET /topup/invoices` itself with a 503 (`PreviewDisabledGuard` —
 * invoices are one of preview's disabled features per CLAUDE.md), rendered as its own notice
 * rather than the default retry card. */
function mountInvoicesTab(container: HTMLElement): () => void {
  return renderAsync<InvoiceEntry[]>(container, {
    load: () => fetchInvoices(),
    isEmpty: (rows) => rows.length === 0,
    renderEmpty(view) {
      view.innerHTML = '';
      view.append(
        emptyState({
          art: 'premium',
          title: t('settings.invoices.empty'),
          hint: t('settings.invoices.emptyBody'),
        }),
      );
    },
    renderError(view, error, retry) {
      view.innerHTML = '';
      if (error instanceof ApiError && error.status === 503) {
        const note = document.createElement('p');
        note.dataset.settings = 'invoices-preview-notice';
        note.className = 'text-[13px] text-text-dim';
        note.textContent = t('settings.invoices.previewDisabled');
        view.append(note);
        return;
      }
      const wrap = document.createElement('div');
      const message = document.createElement('p');
      message.className = 'text-[13px] text-error mb-2';
      message.textContent = errorMessage(error, t('settings.invoices.loadFailed'));
      wrap.append(message, button(t('common.retry'), { variant: 'secondary', onClick: retry }));
      view.append(wrap);
    },
    renderContent(rows, view) {
      view.innerHTML = '';
      const scroller = document.createElement('div');
      scroller.className = 'overflow-x-auto';
      const table = document.createElement('div');
      table.dataset.settings = 'invoices-table';
      table.className = 'min-w-[640px] flex flex-col gap-1.5';

      const head = document.createElement('div');
      head.className =
        'grid grid-cols-[1fr_1.6fr_1fr_1fr_1fr_auto] gap-3 px-3.5 py-2 text-[11px] font-heading uppercase ' +
        'tracking-wide text-text-dim';
      head.append(
        ...headCells([
          [t('settings.table.number'), ''],
          [t('settings.table.description'), ''],
          [t('settings.table.date'), ''],
          [t('settings.table.amount'), 'text-right'],
          [t('settings.table.status'), ''],
          ['', ''],
        ]),
      );
      table.append(head);

      for (const row of rows) {
        const line = document.createElement('div');
        line.className =
          'grid grid-cols-[1fr_1.6fr_1fr_1fr_1fr_auto] gap-3 px-3.5 py-2.5 items-center kit-well';

        const number = document.createElement('span');
        number.className = 'text-sm text-text truncate';
        number.textContent = row.invoiceNumber;

        const description = document.createElement('span');
        description.className = 'text-[12px] text-text-dim truncate';
        description.textContent = row.description;

        const date = document.createElement('span');
        date.className = 'text-[12px] text-text-dim';
        date.textContent = formatDate(row.date, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });

        const amount = document.createElement('span');
        amount.className = 'text-right text-[12px] text-text-dim tabular-nums';
        amount.textContent = `${(row.totalCents / 100).toFixed(2)} ${row.currency}`;

        const statusEl = document.createElement('span');
        statusEl.append(badge(row.status, topupStatusTone(row.status)));

        const actionCell = document.createElement('div');
        actionCell.className = 'flex flex-col items-end gap-1';

        if (!row.downloadUrl) {
          actionCell.append(
            button(t('common.unavailable'), { variant: 'ghost', disabled: true, skewed: false, className: 'px-4' }),
          );
        } else {
          const errLine = document.createElement('span');
          errLine.dataset.settings = `invoice-error-${row.invoiceNumber}`;
          errLine.className = 'text-[11px] text-error hidden';

          const download = button(t('common.download'), {
            variant: 'secondary',
            skewed: false,
            className: 'px-4 min-w-[110px]',
            onClick: () => {
              download.disabled = true;
              download.textContent = t('common.downloading');
              errLine.className = 'text-[11px] text-error hidden';
              void downloadInvoicePdf(row.invoiceNumber)
                .catch((error: unknown) => {
                  errLine.textContent = errorMessage(error, t('settings.invoices.downloadFailed'));
                  errLine.className = 'text-[11px] text-error';
                })
                .finally(() => {
                  download.disabled = false;
                  download.textContent = t('common.download');
                });
            },
          });
          download.dataset.settings = `invoice-download-${row.invoiceNumber}`;
          actionCell.append(download, errLine);
        }

        line.append(number, description, date, amount, statusEl, actionCell);
        table.append(line);
      }

      scroller.append(table);
      view.append(card([scroller], { title: t('settings.invoices.title'), padded: false }));
      requestAnimationFrame(() => revealOnMount(table));
    },
  });
}

/** ── About tab — version and support, unchanged apart from the two links that moved to Papers. ── */
function mountAboutTab(container: HTMLElement, ctx: RouteContext): () => void {
  // Version comes from the build, not a constant in this file, so a deploy can never disagree
  // with the number a player reads here.
  const appVersion = String((import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev');

  const identity = document.createElement('p');
  identity.className = 'kit-well px-3 py-2 min-h-[44px] flex items-center text-[12px] text-text-dim';
  identity.textContent = t('settings.about.signedInAs', {
    name: ctx.user.username ?? ctx.user.email,
    email: ctx.user.email,
  });

  const version = document.createElement('p');
  version.className = 'kit-well px-3 py-2 min-h-[44px] flex items-center text-[12px] text-text-dim';
  version.textContent = t('settings.about.version', { version: appVersion });

  const linkRow = document.createElement('p');
  linkRow.className = 'flex flex-wrap items-center gap-3';
  const support = document.createElement('a');
  support.href = 'mailto:support@blackline.gg';
  // Opened off the page, not in it: a browser with no mail handler (reported 2026-10-09, Windows)
  // navigated the game's own tab to the mailto and left a black page with the music gone.
  support.target = '_blank';
  support.rel = 'noopener';
  plateChrome(support, t('settings.tabs.support'), { variant: 'secondary', className: 'no-underline' });
  linkRow.append(support);

  // The way into the admin dashboard — `/admin`, a route of this SPA (`engine/admin/`), so a
  // plain same-origin anchor is enough: `main.ts` intercepts it and mounts the page. It used to
  // open Kottster in a new tab; Kottster is gone (2026-09-11), and the dashboard is a page of
  // the game an operator is already signed in to.
  if (ctx.user.role !== 'PLAYER') {
    const admin = document.createElement('a');
    admin.href = '/admin';
    admin.dataset.settings = 'admin-panel';
    plateChrome(admin, t('settings.about.adminPanel'), { variant: 'primary', className: 'no-underline' });
    linkRow.append(admin);
  }

  const about = document.createElement('div');
  about.className = 'flex flex-col gap-2';
  about.append(identity, version, linkRow);
  const aboutCard = card([about], { title: t('settings.tabs.about'), headerTrailing: icon('settings', { size: 36, className: 'w-9 h-9 -mr-2' }), className: 'kit-pop' });
  container.append(aboutCard);

  return () => {};
}

export const mountSettingsRoute: RouteMount = (outlet, ctx) => {
  const root = document.createElement('div');
  root.className = 'flex flex-col gap-4';
  outlet.append(root);

  const content = document.createElement('div');

  let active: TabId = 'game';
  let disposeTab: (() => void) | null = null;

  const strip = tabStrip(
    TABS.map((tab) => ({ id: tab.id, label: t(tab.labelKey), hidden: 'hidden' in tab && tab.hidden })),
    active,
    (id) => {
      if (id === active) return;
      active = id as TabId;
      strip.setActive(id);
      mountTab();
    },
  );

  // The Referral tab is the admin's call (`feature.referral`), so it is revealed by the server
  // rather than drawn by default. A failed status read leaves it hidden — the tab's own
  // requests would fail the same way, so there is nothing to show.
  let disposed = false;
  fetchReferralStatus()
    .then((status) => {
      if (!disposed && status.enabled) strip.setVisible('referral', true);
    })
    .catch(() => undefined);

  getWithdrawalLimits()
    .then((limits) => {
      if (!disposed && limits.enabled) strip.setVisible('transactions', true);
    })
    .catch(() => undefined);

  function mountTab(): void {
    disposeTab?.();
    content.innerHTML = '';
    switch (active) {
      case 'game':
        disposeTab = mountGameTab(content);
        break;
      case 'account':
        disposeTab = mountAccountTab(content, ctx);
        break;
      case 'papers':
        disposeTab = mountPapersTab(content);
        break;
      case 'referral':
        disposeTab = mountReferralTab(content);
        break;
      case 'transactions':
        disposeTab = mountTransactionsTab(content, () => {
          active = 'papers';
          strip.setActive('papers');
          mountTab();
        });
        break;
      case 'purchases':
        disposeTab = mountPurchasesTab(content, ctx);
        break;
      case 'invoices':
        disposeTab = mountInvoicesTab(content);
        break;
      case 'about':
        disposeTab = mountAboutTab(content, ctx);
        break;
    }
  }

  root.append(sectionHeading(t('settings.title')), strip.el, content);
  mountTab();

  return () => {
    disposed = true;
    disposeTab?.();
    root.remove();
  };
};
